/**
 * 会话落盘 + **跨标签**刷新协调。
 *
 * ## 为什么需要这一层
 *
 * 后端的 refresh token 是**一次性轮换**的：同一枚 token 被用第二次，后端就
 * 撤销整个 family（`backend/src/auth/service.py::refresh` 的 reuse 分支）。
 * 而刷新这件事从前端看是「每个标签页各自为政」的：
 *
 * - 标签页挂载时无条件刷新一次（旧的 AuthProvider 启动 effect）；
 * - 401 处理器里的并发去重只覆盖**单个标签**（`refreshInFlight` 是组件 ref，
 *   标签之间不共享）。
 *
 * 于是多标签场景（本应用「历史」按钮一点就开一个新标签）在浏览器恢复 /
 * 批量重载时会并发用同一枚 refresh token 刷新：后到的那个请求在服务端看来
 * 是「重放」，于是整个 family 被撤销，前端把令牌清掉，所有标签一起被踢回
 * 登录页。
 *
 * ## 这里的三件事
 *
 * 1. **落盘**：access / refresh / 用户档案（`steam.me`）三件套。档案单独存是
 *    因为 pipe 的 `/auth/me` 只回 `{id, role}`，完整用户行只有 token 响应才
 *    带 —— 不落盘的话，启动时「不刷新」这条路就恢复不出用户。
 * 2. **不验签解码 access token**（只看 `exp`）：用来回答「现在还需不需要刷新」。
 *    前端没有签名密钥，也不该有 —— 这里只做**要不要发网络请求**的判断，
 *    真正的鉴权仍在服务端。
 * 3. **`refreshSession()`**：用 Web Locks API 做的**全浏览器单飞**刷新。
 *    同一时刻只有一个标签真的打 `/auth/refresh`；其余标签进锁后发现 token
 *    已被换新，直接采纳 localStorage 里的新令牌对，**不发网络请求**。
 *
 * ## 失败分档（为什么不是「失败 = 登出」）
 *
 * 刷新失败**不等于**登录态失效。后端只有两种回答有意义：
 *
 * - **明确拒绝**（401 / 403）→ refresh token 确实废了 → 可以清场；
 * - **说不清**（断网、后端重启、5xx、限流）→ 后端**根本没表态** → 清场
 *   就是把一次网络抖动翻译成「用户被踢回登录页」。
 *
 * 所以 `refreshSession()` 返回**三档**（见 `RefreshOutcome`），由调用方
 * switch 处理：成功 → 继续用；拒绝 → 清场；暂不可用 → **登录态只是「暂缓
 * 验证」，不是「已确认失效」**，一律不清场。
 */
import { apiPost } from '../api/http';
import { ERROR_CODE, toHttpError, type ErrorCode } from '../api/errors';
import { EP } from '../api/wire';
import type { Res } from '../api/wire';
import type { Role } from '../domain/enums';
import { narrowRole } from './role';

export const TOKEN_KEY = 'steam.access_token';
export const REFRESH_KEY = 'steam.refresh_token';
/** 用户档案（Me 的 JSON）。启动时「不刷新」这条路靠它恢复登录态。 */
export const ME_KEY = 'steam.me';
/** Web Locks 的锁名 —— 同一浏览器内所有标签共用一把。 */
export const REFRESH_LOCK_NAME = 'steam.auth.refresh';

export interface Me {
  id: string;
  username: string;
  role: Role;
  grants_mask: number;
  email: string | null;
  created_at: string;
  updated_at: string;
}

/** token 响应里带的完整用户行（`/auth/login` 与 `/auth/refresh` 同一形状）。 */
type TokenPair = NonNullable<Res<typeof EP.authLogin, 'post'>>;
export type TokenProfile = TokenPair['user'];

export function toMe(profile: TokenProfile): Me {
  return {
    id: profile.id,
    username: profile.username,
    role: narrowRole(profile.role),
    grants_mask: profile.grants_mask ?? 0,
    email: profile.email ?? null,
    created_at: profile.created_at,
    updated_at: profile.updated_at,
  };
}

/* ------------------------------------------------------------------ 落盘 */

export function readAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function readRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

/**
 * 读回落盘的用户档案。
 *
 * 档案可能被写坏（手动改 localStorage、跨版本残留、旧格式）—— 读取一律
 * **容忍损坏**：解析不出来就当没有，让调用方走刷新那条路重新拿一份，而不是
 * 让整个应用起不来。
 */
export function readMe(): Me | null {
  const raw = localStorage.getItem(ME_KEY);
  if (raw === null) return null;
  try {
    return parseMe(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

function parseMe(value: unknown): Me | null {
  if (typeof value !== 'object' || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.id !== 'string' || typeof row.username !== 'string') return null;
  if (typeof row.role !== 'number') return null;
  if (typeof row.created_at !== 'string' || typeof row.updated_at !== 'string') return null;
  return {
    id: row.id,
    username: row.username,
    role: narrowRole(row.role),
    // 旧档案没有这一列 ⇒ 0（无授权）——下次 token 刷新会带上真值
    grants_mask: typeof row.grants_mask === 'number' ? row.grants_mask : 0,
    email: typeof row.email === 'string' ? row.email : null,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function writeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}

/**
 * 落盘一次完整的会话（令牌对 + 档案）。
 *
 * **顺序有讲究**：档案先写、refresh token 后写。refresh token 是别的标签
 * 「发现已被轮换」的**提交点** —— 谁读到了新的 refresh token，就一定也能读到
 * 配套的档案（否则那个标签会以为「有令牌没档案」，把共享的会话误清掉）。
 */
export function storeSession(accessToken: string, refreshToken: string, me: Me | null): void {
  if (me !== null) writeMe(me);
  writeTokens(accessToken, refreshToken);
}

export function writeMe(me: Me): void {
  localStorage.setItem(ME_KEY, JSON.stringify(me));
}

/** 清场 —— 令牌与档案一起清。档案不清的话下次启动会恢复出一个「有 me 无令牌」的怪状态。 */
export function clearStoredSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(ME_KEY);
}

/* --------------------------------------------------- access token 解码 */

/**
 * 解出 access token 的 `exp`（epoch 毫秒），解不出来返回 `null`。
 *
 * **只解码、不验签**：前端没有签名密钥，也不该有。这里唯一的用途是判断
 * 「现在还需不需要刷新」—— 服务端照样会独立校验每一个请求。
 */
export function decodeAccessExpiry(token: string): number | null {
  const segments = token.split('.');
  if (segments.length < 2) return null;
  const payload = decodeBase64Url(segments[1] ?? '');
  if (payload === null) return null;
  try {
    const parsed: unknown = JSON.parse(payload);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const exp = (parsed as { exp?: unknown }).exp;
    if (typeof exp !== 'number' || !Number.isFinite(exp)) return null;
    return exp * 1000;
  } catch {
    return null;
  }
}

function decodeBase64Url(segment: string): string | null {
  try {
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    // 用 decodeURIComponent 转 UTF-8，而不是 TextDecoder —— jsdom 未必有它。
    let encoded = '';
    for (const byte of bytes) encoded += '%' + byte.toString(16).padStart(2, '0');
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

/* ----------------------------------------------------------- 刷新单飞 */

/**
 * 刷新结果 —— **显式三档**，不用裸 boolean。
 *
 * 调用方必须 switch 三档，每一档的处置完全不同：
 *
 * | 档位 | 含义 | 调用方该做什么 |
 * |---|---|---|
 * | `success` | 拿到了可用的 access token | 换上它，继续用 |
 * | `rejected` | **后端明确拒绝**了 refresh token | 登录态确认失效 → 清场 |
 * | `unavailable` | 后端**没表态**（断网 / 5xx / 限流 / 锁故障） | 登录态只是「暂缓验证」→ **不清场** |
 */
export type RefreshOutcome =
  /**
   * 成功。本标签真的换了新令牌（`source: 'refreshed'`，已写回 localStorage），
   * 或采纳了别的标签换新的令牌（`source: 'adopted'`，没发网络请求）。
   */
  | { status: 'success'; accessToken: string; me: Me | null; source: 'refreshed' | 'adopted' }
  /** 后端明确拒绝（401 / 403）—— refresh token 确实废了，**只有这一档允许清场**。 */
  | { status: 'rejected'; reason: RefreshRejectionReason }
  /** 瞬时故障 —— 后端没表态，**不清场、不清 localStorage**。 */
  | { status: 'unavailable'; reason: UnavailableReason };

/** 「后端明确拒绝」的原因：只有这两种错误码。 */
export type RefreshRejectionReason =
  | typeof ERROR_CODE.unauthenticated
  | typeof ERROR_CODE.forbidden;
/** 「后端没表态」的原因：排除掉上面两种拒绝码的其余错误码。 */
export type UnavailableReason = Exclude<ErrorCode, RefreshRejectionReason>;

/**
 * 把一次失败分类成「拒绝」还是「暂不可用」。
 *
 * 判据只有一条：**后端有没有明确表态**。
 *
 * - `unauthenticated`（401）/ `forbidden`（403）→ 明确拒绝 → 清场；
 * - 其余（`transport` 断网、`internal` 5xx、`rateLimited` 429、
 *   `dependencyUnavailable` 503、`unknown` …）→ 后端没表态 → 不清场。
 *
 * `toHttpError` 兜住了非 `HttpError` 的抛出：原生 `fetch` 的网络错误会变成
 * `transport`（status 0），所以「断网」天然落在「暂不可用」这一档。
 *
 * ⚠️ **依赖「后端的 401 一定落回 `unauthenticated`」**。后端 auth 层的错误码
 * 是 `token_reuse` / `token_revoked` / `invalid_credentials`（`backend/src/auth/
 * exceptions.py`），**不在**前端 `ERROR_CODE` 词表里 —— `errorFromResponse`
 * 于是按 HTTP 状态回落成 `unauthenticated`，正好是「明确拒绝」那一档。
 * 若哪天把这些码加进 `ERROR_CODE` 词表而不同步这里，拒绝档会掉进
 * 「暂不可用」—— 用户永远登不出去。
 */
function classifyFailure(cause: unknown): RefreshOutcome {
  const code = toHttpError(cause).code;
  if (code === ERROR_CODE.unauthenticated || code === ERROR_CODE.forbidden) {
    return { status: 'rejected', reason: code };
  }
  return { status: 'unavailable', reason: code };
}

/**
 * 刷新会话 —— **全浏览器单飞**。
 *
 * 流程：
 *
 * 1. 锁外读一次 refresh token（记作 `observed`）；
 * 2. 拿到 `steam.auth.refresh` 锁后，**重读** localStorage：
 *    - 与 `observed` 不同 → 别的标签刚轮换过 → 采纳新令牌，**不发网络请求**；
 *    - 相同 → 真的打一次 `/auth/refresh`，成功写回 localStorage；
 * 3. 刷新失败时**先不急着下结论**：再读一次 localStorage，若 refresh token
 *    已被别的标签换新（与我们刚试的那枚不同），采纳它并按成功返回；确实还是
 *    同一枚，才按错误码分档返回 `rejected` / `unavailable`。
 *
 * 锁的范围只包「读 → 网络 → 写」，状态更新（`setAccessToken` / `setMe`）在
 * 锁外做 —— 否则一次刷新会把整条回调变成串行队列，后来的标签全排在后面等。
 */
export async function refreshSession(): Promise<RefreshOutcome> {
  const observed = readRefreshToken();
  // 盘上根本没有 refresh token → 没有会话可续（不是「暂时续不了」）。
  if (observed === null) return { status: 'rejected', reason: ERROR_CODE.unauthenticated };
  const locks = lockManager();
  if (locks === null) {
    // 降级：环境没有 Web Locks API（老 Safari / 非安全上下文）。
    // 此时跨标签去重无从谈起，退回「直接刷新」——也就是旧行为。
    // 这是**能力缺失**而不是新引入的风险：同一枚 token 被用两次本来就该
    // 撤销 family，所以宁可让老浏览器继续踩这个坑，也不要在这里造一套
    // 假的「以为去重过了」的语义。
    return await performRefresh(observed);
  }
  try {
    return await locks.request(REFRESH_LOCK_NAME, async () => {
      const current = readRefreshToken();
      if (current === null) return { status: 'rejected', reason: ERROR_CODE.unauthenticated };
      if (current !== observed) {
        const adopted = adopt();
        if (adopted !== null) return adopted;
        // 别的标签换过新令牌却没留下 access token（盘上状态被写坏）——
        // 拿那枚新 refresh token 自己刷一次（还在锁内，不会与别人并发）。
        return await performRefresh(current);
      }
      return await performRefresh(current);
    });
  } catch (cause) {
    // 锁本身出问题（极端情况下 request 会 reject）。
    // **不在这里补一次直接刷新** —— 那可能拿一枚已经被别的标签轮换掉的旧
    // token 去请求，等于自己踩「重放」。先看盘上有没有别人换新的，有就采纳；
    // 没有就按错误码分档 —— 锁故障说明的是「这次没能拿到互斥」，
    // 不是「令牌已失效」，所以归到「暂不可用」，**不清场**。
    const current = readRefreshToken();
    if (current !== null && current !== observed) {
      const adopted = adopt();
      if (adopted !== null) return adopted;
    }
    return classifyFailure(cause);
  }
}

/**
 * 采纳别的标签写进 localStorage 的新令牌 —— 不发网络请求。
 *
 * 返回 `null` 表示「盘上只有 refresh token、没有 access token」，采纳不了。
 */
function adopt(): RefreshOutcome | null {
  const accessToken = readAccessToken();
  if (accessToken === null) return null;
  return { status: 'success', accessToken, me: readMe(), source: 'adopted' };
}

async function performRefresh(refreshToken: string): Promise<RefreshOutcome> {
  try {
    const tokens = await apiPost(EP.authRefresh, {
      body: { refresh_token: refreshToken },
      /**
       * **必须显式带 bearer** —— 后端 `/auth/refresh` 要求请求头里有
       * `Authorization: Bearer …`（`backend/src/auth/router.py::refresh_endpoint`：
       * 它故意**不校验** access 的有效性，只要头在，因为「access 刚过期时正是
       * 最需要轮换的时候」）。
       *
       * 为什么不能靠内存里那枚：`AuthProvider` 的启动恢复路径**只在 access 未过期
       * 时**才把盘上那枚装进内存（`setAccessToken`）——access 已过期 ⇒ 内存里是
       * `null` ⇒ 请求不带头 ⇒ 后端 401 ⇒ 本模块按「后端明确拒绝」归入 `rejected`
       * ⇒ 上层 `clearSession()` ⇒ **用户被踢回登录页**（2026-10-23 真机复现：
       * 页面开着超过 30 分钟再切回来，就得重新登录）。
       *
       * ⇒ 这里带上**盘上那枚**（可能已过期，但它仍是合法 bearer；后端不校验它）。
       */
      bearer: readAccessToken(),
    });
    const me = toMe(tokens.user);
    storeSession(tokens.access_token, tokens.refresh_token, me);
    return { status: 'success', accessToken: tokens.access_token, me, source: 'refreshed' };
  } catch (cause) {
    // 失败后**不急着清场**：再读一次 localStorage。若 refresh token 已被
    // 别的标签换新（与我们刚试的那枚不同），失败的是「我们这一枚旧 token」
    // 而不是「整个会话已死」—— 采纳新 token，按成功返回。
    const current = readRefreshToken();
    if (current !== null && current !== refreshToken) {
      const adopted = adopt();
      if (adopted !== null) return adopted;
    }
    // 分档：只有后端明确拒绝（401/403）才允许调用方清场；断网 / 5xx / 限流
    // 一律「暂不可用」—— 那只是这次没刷成，不是「登录态已死」。
    return classifyFailure(cause);
  }
}

function lockManager(): LockManager | null {
  if (typeof navigator === 'undefined') return null;
  // 非安全上下文（http://）里 navigator.locks 根本不存在，TS 却把它声明成
  // 必有字段 —— 所以这里走 unknown 断言做**运行时**判断。
  const locks = (navigator as unknown as { locks?: LockManager }).locks;
  if (locks === undefined || locks === null) return null;
  if (typeof locks.request !== 'function') return null;
  return locks;
}
